// Tratamientos v0 (findings #103). The treatment is saved on the server (online,
// office only); the stock used comes out of the Inventario "Químicos" item and
// its cost is booked as Salud (H) with sourceEventType 'treatment' — both
// through the existing offline-safe Inventario / Costos pipelines.

import { api, type TreatmentCreatePayload, type TreatmentRecord } from './api'
import { inventoryApi } from './inventoryApi'
import { lotCostEventApi } from './lotCostEventApi'
import { createId } from './id'
import type { InventoryItem } from '../types/inventory'

export type { TreatmentRecord, TreatmentMethod } from './api'

export const TREATMENT_METHODS = ['bath', 'feed', 'injection', 'other'] as const

const uuid = (): string => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : createId('trt'))

/** Cost of the quantity used at the item's unit cost, in cents; null without a price. */
export const treatmentCostCents = (quantityUsed: number | undefined, item: Pick<InventoryItem, 'unitCostCents'> | null | undefined): number | null => {
  if (!item || typeof item.unitCostCents !== 'number' || item.unitCostCents <= 0) return null
  if (typeof quantityUsed !== 'number' || !(quantityUsed > 0)) return null
  return Math.round(quantityUsed * item.unitCostCents)
}

/** The treatment whose withdrawal period is still running on `atIso`, latest end first. */
export const activeWithdrawal = (treatments: TreatmentRecord[], atIso: string): TreatmentRecord | null => {
  const at = Date.parse(atIso)
  if (!Number.isFinite(at)) return null
  const running = treatments
    .filter((treatment) => !treatment.deletedAt && treatment.withdrawalEndsAt && Date.parse(treatment.withdrawalEndsAt) > at)
    .sort((a, b) => Date.parse(b.withdrawalEndsAt ?? '') - Date.parse(a.withdrawalEndsAt ?? ''))
  return running[0] ?? null
}

export const treatmentApi = {
  list: (params: { farmId?: string; lotId?: string; includeDeleted?: boolean }) => api.treatments.list(params),

  /**
   * Saves the treatment, then takes the quantity out of stock and books its
   * cost. The treatment is the record that matters (GLOBALG.A.P.); stock and
   * cost follow and are retried by their own outboxes.
   */
  record: async (args: {
    payload: Omit<TreatmentCreatePayload, 'id' | 'costEventId'>
    item: InventoryItem | null
    currency: string
  }): Promise<TreatmentRecord> => {
    const id = uuid()
    const cents = treatmentCostCents(args.payload.quantityUsed, args.item)
    const costEventId = cents !== null ? uuid() : undefined
    const saved = await api.treatments.create({ ...args.payload, id, ...(costEventId ? { costEventId } : {}) })

    if (args.item && typeof args.payload.quantityUsed === 'number' && args.payload.quantityUsed > 0) {
      const metadata = { ...(args.item.metadata ?? {}) }
      const usages = Array.isArray(metadata.treatmentUsages) ? metadata.treatmentUsages : []
      metadata.treatmentUsages = [...usages, { treatmentId: id, date: args.payload.appliedAt, quantity: args.payload.quantityUsed }]
      await inventoryApi.upsert({
        ...args.item,
        onHand: Math.max(0, Math.round((args.item.onHand - args.payload.quantityUsed) * 1000) / 1000),
        metadata,
        updatedAt: new Date().toISOString()
      })
    }

    if (cents !== null && costEventId) {
      await lotCostEventApi.create({
        id: costEventId,
        farmId: args.payload.farmId,
        lotId: args.payload.lotId,
        productionUnitId: args.payload.productionUnitIds.length === 1 ? args.payload.productionUnitIds[0] : undefined,
        recordedAt: args.payload.appliedAt,
        categoryPrefix: 'H',
        amountCents: cents,
        currency: (args.item?.currency || args.currency).toUpperCase(),
        sourceEventType: 'treatment',
        sourceEventId: id,
        notes: `${args.payload.productName}${args.payload.dose ? ` · ${args.payload.dose}` : ''}`
      })
    }
    return saved
  },

  /** Marks the treatment deleted (kept for traceability), returns the stock and removes its cost. */
  remove: async (treatment: TreatmentRecord, reason: string, items: InventoryItem[]): Promise<TreatmentRecord> => {
    const removed = await api.treatments.remove(treatment.id, reason)
    const item = treatment.inventoryItemId ? items.find((candidate) => candidate.id === treatment.inventoryItemId) : null
    if (item && typeof treatment.quantityUsed === 'number' && treatment.quantityUsed > 0) {
      await inventoryApi.upsert({
        ...item,
        onHand: Math.round((item.onHand + treatment.quantityUsed) * 1000) / 1000,
        updatedAt: new Date().toISOString()
      })
    }
    if (treatment.costEventId) {
      await lotCostEventApi.delete(treatment.costEventId).catch(() => undefined)
    }
    return removed
  }
}
